/* eslint-disable react-hooks/exhaustive-deps */
import { Menu, type MenuListProps, type MenuProps } from '@mui/material';
import { useEffect, useState } from 'react';
import type React from 'react';

type MenuWithLazyPersistedMountingProps = Omit<
  MenuProps,
  'slotProps' | 'onKeyDownCapture' | 'onKeyUpCapture' | 'keepMounted'
> & {
  labelledById: string;
  slotProps?: {
    list: Omit<MenuListProps, 'aria-labelledby'>;
  };
};

// Once this menu has been mounted, it stays mounted. This is useful to persist
// menu child component states, while avoiding unnecessarily mounting a bunch
// of menus the user doesn't interact with.
const MenuWithLazyPersistedMounting = ({
  open,
  labelledById,
  slotProps,
  ...props
}: MenuWithLazyPersistedMountingProps) => {
  const [hasBeenOpen, setHasBeenOpen] = useState(false);

  useEffect(() => {
    if (open && !hasBeenOpen) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setHasBeenOpen(true);
    }
  }, [open]);

  return (
    <Menu
      open={open}
      slotProps={{
        list: {
          'aria-labelledby': labelledById,
          ...slotProps?.list,
        },
      }}
      // This is necessary to make error-related (retry, dismiss) buttons
      // reachable with the Tab key
      onKeyDownCapture={(e: React.KeyboardEvent<HTMLDivElement>): void => {
        if (e.key === 'Tab') {
          e.stopPropagation();
        }
      }}
      onKeyUpCapture={(e: React.KeyboardEvent<HTMLDivElement>): void => {
        if (e.key === 'Tab') {
          e.stopPropagation();
        }
      }}
      keepMounted={open || hasBeenOpen}
      {...props}
    />
  );
};

export default MenuWithLazyPersistedMounting;
